import { createStackNavigator } from "@react-navigation/stack";
import { AHEmptyScreen, AHSeparator, Box, ListItem, useTheme } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { keyExtractorId } from "../../../../common/keyExtractors";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";

import { WorkspaceRoutineTemplatesList_workspace$key } from "./__generated__/WorkspaceRoutineTemplatesList_workspace.graphql";
import { WorkspaceRoutineTemplatesQuery } from "./__generated__/WorkspaceRoutineTemplatesQuery.graphql";

import { WorkspaceRoutineTemplateItemRowContainer } from "./WorkspaceRoutineItemRowContainer";

import { OrgSearchInput } from "../../OrgSearchInput";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import { useWorkspaceId } from "../../workspaceId";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { WorkspaceRoutineTemplatesListItem } from "./WorkspaceRoutineTemplateListItem";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";
import { UserLoggedInNavigatorNavigationProp } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

function List(props: {
  workspace: WorkspaceRoutineTemplatesList_workspace$key;
  openTemplateDetail: (routineId: string) => void;
  openRoutineEdit: (routineId: string) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [order, setOrder] = React.useState<"ASC" | "DESC">("DESC");
  const [orderBy, setOrderBy] = React.useState<"title" | "topic">("title");

  const data = useFragment(
    graphql`
      fragment WorkspaceRoutineTemplatesList_workspace on Workspace {
        id
        topics {
          __typename
        }

        routines(type: TEMPLATE) {
          ...WorkspaceRoutineTemplateListItem_Routine
          id
          title
          orgTopic {
            name
          }
        }
      }
    `,
    props.workspace
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const routines = useMemo(() => data.routines ?? [], [data.routines]);
  const listStyle = useOrgListStyleProps();

  type Item = (typeof routines)[number];
  const sortFunction = useMemo(() => {
    switch (orderBy) {
      case "title":
        return (a: Item, b: Item) => b.title.localeCompare(a.title);
      case "topic":
        return (a: Item, b: Item) => {
          // If both have topics, compare them
          if (a.orgTopic?.name && b.orgTopic?.name) {
            return a.orgTopic.name.localeCompare(b.orgTopic.name);
          }
          // If only one has a topic, prioritize the one with a topic
          if (a.orgTopic?.name) return -1;
          if (b.orgTopic?.name) return 1;
          // If neither has a topic, fall back to title comparison
          return a.title.localeCompare(b.title);
        };
    }
  }, [orderBy]);

  const listData = useMemo(
    () =>
      data.routines
        ?.filter((r) =>
          r.title.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
        .sort((a, b) => sortFunction(a, b) * (order === "ASC" ? -1 : 1)) ?? [],
    [data.routines, search, sortFunction, order]
  );

  const intl_tokens = useIntlTokens();

  const hasTopics = data?.topics ? data.topics.length > 0 : false;
  const ListHeaderComponent = useMemo(() => {
    return (
      <Box spaceTop="medium">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <ListItem displayMode="TABLE" disabled border="border-primary">
          <WorkspaceRoutineTemplateItemRowContainer
            title={
              <SortableColumnTitle
                title={intl_tokens.Name}
                onOrderBySelect={() => {
                  setOrderBy("title");
                }}
                order={orderBy === "title" ? order : null}
                onOrderChange={setOrder}
              />
            }
            topic={
              hasTopics ? (
                <SortableColumnTitle
                  title={intl_tokens.Topic}
                  onOrderBySelect={() => {
                    setOrderBy("topic");
                  }}
                  order={orderBy === "topic" ? order : null}
                  onOrderChange={setOrder}
                />
              ) : null
            }
            more={null}
          />
        </ListItem>
        <AHSeparator />
      </Box>
    );
  }, [search, intl_tokens.Name, intl_tokens.Topic, orderBy, order, hasTopics]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <WorkspaceRoutineTemplatesListItem
          workspaceRoutine={item}
          isLast={index === listData.length - 1}
          openTemplateDetail={props.openTemplateDetail}
          openRoutineEdit={props.openRoutineEdit}
          hasTopics={hasTopics}
        />
      );
    },
    [
      listData.length,
      props.openTemplateDetail,
      props.openRoutineEdit,
      hasTopics,
    ]
  );

  if ((data.routines?.length ?? 0) === 0) {
    return (
      <AHEmptyScreen
        title={intl_tokens.RoutineLibrary}
        subtitle={
          <FormattedMessage
            defaultMessage="Here it is possible to create routines that units can voluntarily choose to adopt and modify."
            description="Routine library empty state subtitle"
          />
        }
        illustration={
          <Image
            contentFit="contain"
            style={{ flex: 1 }}
            source={require("./RoutineLibrary.svg")}
          />
        }
      />
    );
  }

  return (
    <FlatList
      {...listStyle}
      ListEmptyComponent={<WorkspaceEmptyListItem />}
      ListHeaderComponent={ListHeaderComponent}
      stickyHeaderIndices={[0]}
      keyExtractor={keyExtractorId}
      data={listData}
      ItemSeparatorComponent={AHSeparator}
      renderItem={renderItem}
    />
  );
}

function InnerWorkspaceRoutineTemplates({
  fetchKey,
  ...props
}: {
  fetchKey: string;
  navigation: UserLoggedInNavigatorNavigationProp<"OrgHome">;
}) {
  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<WorkspaceRoutineTemplatesQuery>(
    graphql`
      query WorkspaceRoutineTemplatesQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          id
          ...WorkspaceRoutineTemplatesList_workspace
        }
      }
    `,
    {
      workspaceId,
    },
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  return data.workspace.id ? (
    <List
      workspace={data.workspace}
      openTemplateDetail={(routineId) => {
        (props.navigation.navigate as any)("WorkspaceRoutineTemplateDetail", {
          routineId,
        });
      }}
      openRoutineEdit={(routineId) => {
        props.navigation.navigate("RoutineEdit", {
          orgWorkspaceId: workspaceId,
          id: routineId,
        });
      }}
    />
  ) : null;
}

function WorkspaceRoutineTemplatesMainScreen(props: {
  navigation: UserLoggedInNavigatorNavigationProp<"OrgHome">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceRoutineTemplates {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const WorkspaceRoutineStack = createStackNavigator();

export function WorkspaceRoutineTemplatesScreen() {
  const theme = useTheme();

  return (
    <WorkspaceRoutineStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: theme["surface-background-light"],
        },
        cardStyle: {
          backgroundColor: theme["surface-background-light"],
        },
      }}
    >
      <WorkspaceRoutineStack.Screen
        name="WorkspaceRoutineTemplatesMain"
        component={WorkspaceRoutineTemplatesMainScreen}
        options={{ headerShown: false }}
      />
    </WorkspaceRoutineStack.Navigator>
  );
}
